import { FileIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFileValidation } from '@/features/documents/util/file-util';
import {
  allowedTypes,
  FileType,
  getFileSize,
  getReadableType,
  useFileTypeDisplay,
} from '@/features/documents/util/mimetype-util';
import PlusIcon from '@/shared/assets/plus.svg?react';
import PurpleUploadIcon from '@/shared/assets/purple_upload_icon.svg?react';
import XIcon from '@/shared/assets/xmark_filled.svg?react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { DocumentData } from '@/shared/components/ui/upload/document-upload-dialog';
import { cn } from '@/shared/utils/cn';

export const FileInputField = ({
  handleFileChange,
  file,
  document,
}: {
  handleFileChange: (file: File | null) => void;
  file?: File | null;
  document?: DocumentData;
}) => {
  const fileUploadRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const { validateFile } = useFileValidation();

  const { t: tDocument } = useTranslation('document', {
    keyPrefix: 'FileInputForm.upload_file',
  });

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const isValid = validateFile(droppedFile);

      if (!isValid) return;
      handleFileChange(droppedFile);
      const newPreviewUrl = URL.createObjectURL(droppedFile);
      setPreviewUrl(newPreviewUrl);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // Handle file from document when editing newly created documents
  useEffect(() => {
    if (file) {
      const newPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(newPreviewUrl);
    }
  }, [file]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileChange(file);
      const newPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(newPreviewUrl);
    }
  };

  return (
    <div className="flex flex-col items-start gap-6">
      <Card
        variant="information"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          'h-[238px] transition-all duration-200',
          file ? 'w-[80%] max-w-[80%]' : 'w-full max-w-full',
          isDragging && 'border-dashed border-white/50 bg-white/5',
        )}
      >
        <CardContent className="h-full p-6">
          {file && previewUrl && (
            <FilePreview
              file={file}
              previewUrl={previewUrl}
              onDelete={() => handleFileChange(null)}
            />
          )}
          {document && !file ? (
            <FilePreview
              document={document}
              previewUrl={previewUrl}
              onDelete={() => handleFileChange(null)}
            />
          ) : (
            !file &&
            !document && (
              <FileUploadArea
                fileUploadRef={fileUploadRef}
                onFileChange={onFileChange}
              />
            )
          )}
        </CardContent>
      </Card>
      {!file && !document && (
        <p className="w-full text-center text-4xs text-custom-grey">
          {tDocument('allowed_types', {
            types: allowedTypes.map((type) => getReadableType(type)).join(', '),
          })}
        </p>
      )}
    </div>
  );
};

const FileUploadArea = ({
  fileUploadRef,
  onFileChange,
}: {
  fileUploadRef: React.RefObject<HTMLInputElement>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const { t: tDocument } = useTranslation('document', {
    keyPrefix: 'FileInputForm.upload_file',
  });
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4">
      <PurpleUploadIcon className="size-[54px]" />
      <p className="text-xs font-[590]">{tDocument('upload_hint')}</p>
      <Button
        variant="greenGradient"
        btnType="secondary"
        onClick={(e) => {
          e.preventDefault();
          fileUploadRef.current?.click();
        }}
      >
        <PlusIcon className="!size-[18px]" />
        {tDocument('add_file')}
      </Button>
      <input
        id="file-input"
        type="file"
        ref={fileUploadRef}
        onChange={onFileChange}
        hidden
        accept={allowedTypes.join(',')}
      />
    </div>
  );
};

const FilePreview = ({
  file,
  document,
  previewUrl,
  onDelete,
}: {
  file?: File;
  document?: DocumentData;
  previewUrl?: string | null;
  onDelete: () => void;
}) => {
  const { getFileTypeDisplay } = useFileTypeDisplay();

  return (
    <div className="flex flex-row items-end gap-6">
      <Card className="relative !h-[190px] !min-w-[150px] !max-w-[150px] overflow-hidden rounded-[10px]">
        {!document && (
          <XIcon
            className="absolute right-1 top-1 size-8 cursor-pointer rounded-full p-1 text-white"
            onClick={onDelete}
          />
        )}
        {document && 'file' in document && document.file ? (
          document.file.type.startsWith('image/') ? (
            <img
              src={previewUrl || ''}
              alt="Preview"
              className="h-full w-full object-cover"
            />
          ) : document.file.type === 'application/pdf' ? (
            <embed src={previewUrl || ''} className="h-full w-full" />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center bg-custom-extra-light-grey">
              <FileIcon className="size-10" />
            </div>
          )
        ) : document ? (
          <div className="flex h-full w-full flex-col items-center justify-center bg-custom-extra-light-grey">
            <FileIcon className="size-10" />
          </div>
        ) : file ? (
          file.type.startsWith('image/') ? (
            <img
              src={previewUrl || ''}
              alt="Preview"
              className="h-full w-full object-cover"
            />
          ) : file.type === 'application/pdf' ? (
            <embed src={previewUrl || ''} className="h-full w-full" />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center bg-custom-extra-light-grey">
              <FileIcon className="size-10" />
            </div>
          )
        ) : null}
      </Card>
      <div className="min-w-0">
        <p className="truncate text-xl font-bold">
          {document?.file_path?.split('/').pop() ||
            document?.title ||
            file?.name}
        </p>
        <div className="flex flex-row gap-2 text-4xs text-custom-grey">
          {document && (
            <p>{getFileTypeDisplay(document.type as FileType).label}</p>
          )}
          {file && (
            <p>
              {getReadableType(file.type)} | {getFileSize(file.size)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
